// Brief 85 §4 — CaseModal, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN. The statement block below is byte-identical to the body
// of the ca clause as it stood in App.tsx —
//   sha256 8280abad63e210dcf20f27b97b0355cfec62d3bf8d3bbc87e68aed10890f4914
// — indentation included, which is why it sits at the depth it had inside
// App(). Only the imports, the Props type and the signature are new. Do not
// reformat: the indentation IS the proof.
//
// Props are the UNION of the App-scope names the body actually read, from a
// TypeScript-checker walk rather than chosen. The dialog declares no hook (§0
// measured zero across all 21 blocks), so it moves as a component without any
// state ownership changing hands.

/* 189 §B — `import React` is react/react-in-jsx-scope's OWN remedy, counted per
   JSX element (168A's method): the block below would have been +1 on a file
   holding that rule's warnings, and the import retires every one of them. */
import React from "react";
import Combobox from "../../components/Combobox";
import {
  api, // 174 §1.2 — the folder calls go through the one door, which sends BOTH credentials
  convertCase,
  convertCaseUndo,
  convertCaseFinalize,
} from "../../api";
import MultiSelect from "../../components/MultiSelect";
import { AddButton, ModalLayout } from "../../components/SharedUI";
import CustomFieldsBlock from "../../components/CustomFieldsBlock"; // 189 §B
import AmountField from "../AmountField"; // 207C §2 — the rate card's field, moved
import { CT, FS, RAD, Z } from "../../lib/tokens";
import { ExternalLink as ExtLink2, Folder, Link2, Pencil, X } from "lucide-react";
import { SK, fc, fq } from "../../utils";
import { tempId } from "../../lib/tempId"; // 207 §1.2
import { choosableCo } from "../../lib/collaboratorState";
// 191B §A (item 8) — a form with nothing stored DISPLAYS the one default the
// money counts with (src/lib/successFee.mjs); `?? 15` stood here before.
import { successFeeRateOf } from "../../lib/successFee.mjs";
import { openOneDrivePicker } from "../../lib/loadOnUse.mjs"; // 208 §1 — the picker loads the first time it opens
import type { TFn, TaxList } from "../../lib/taxonomyTypes";
import FieldSelect from "../FieldSelect"; // 154B §2
import { useTranslation } from "../../i18n";
import { languageOptions } from "../../lib/caseLanguage"; // 209 §5 — the dictionary's languages, named by the platform
import { normalizeLang, storedClientLanguage, storedCaseLanguage } from "../../lib/docLang.mjs"; // 209 §5
import type { FieldSelectOption } from "../FieldSelect";
import type { makeModalFields } from "../modalFields";

type Vocab = ReturnType<typeof makeModalFields>;

// 140 §2 — the slice of a shaped case row the convert flow reads and writes
// back into `cases`/`form`. The rows carry dozens more fields; the merge
// spreads the whole response row into `cases` and only these into `form`
// (a wholesale form overwrite would clobber in-flight edits of other fields).
type ConvertedCaseRow = {
  id?: number;
  ref?: string | null;
  status?: string | null;
  isProspect?: boolean;
  formerRef?: string | null;
  pendingDate?: string | null;
  closedDate?: string | null;
  archivedDate?: string | null;
} & Record<string, unknown>;
// Modal date inputs bind "YYYY-MM-DD"; the API answers full ISO datetimes.
const isoDay = (v: unknown): string | null =>
  typeof v === "string" && v ? v.slice(0, 10) : null;

type Props = {
  F: Vocab["F"];
  S: Vocab["S"];
  T: Vocab["T"];
  _fl: Vocab["_fl"];
  _mt: Vocab["_mt"];
  _appIsAdmin: boolean;
  _appPerms: any;
  caseModalCalc: any;
  caseStatusesList: readonly FieldSelectOption[];
  caseTypeEntries: TaxList;
  caseTypesList: readonly FieldSelectOption[];
  cases: any[];
  /** 209 §5 — the firm's clients: a new case's language is offered from its first client's. */
  clients: unknown;
  cm: () => void;
  collaborators: any[];
  collectiveCases: any[];
  confirmAndUndo: (o: { label?: unknown; title?: string; message?: string; confirmLabel?: string; onConfirm: () => void; undo?: { snapshot: unknown; restore: (snap: unknown) => void } | null; toastName?: unknown; archive?: unknown; windowMs?: number; onExpire?: () => void }) => void;
  contactPersons: any[];
  form: any;
  jurisdictions: any[];
  persist: (k: string, d: unknown, fn: unknown, opts?: unknown) => Promise<unknown>;
  setCases: (v: unknown) => void;
  setForm: (v: unknown) => void;
  t: TFn;
  toast: (m?: string, type?: string, opts?: { action?: unknown }) => void;
  updateField: (k: string, v: unknown) => void;
};

export default function CaseModal({
  F,
  S,
  T,
  _fl,
  _mt,
  _appIsAdmin,
  _appPerms,
  caseModalCalc,
  caseStatusesList,
  caseTypeEntries,
  caseTypesList,
  cases,
  clients,
  cm,
  collaborators,
  collectiveCases,
  confirmAndUndo,
  contactPersons,
  form,
  jurisdictions,
  persist,
  setCases,
  setForm,
  t,
  toast,
  updateField,
}: Props) {
  /* 209 §5 [RULED 18] — THE CASE'S LANGUAGE: required for a new case, chosen
     among the languages the dictionary carries (named by the platform in the
     reader's language), OFFERED FROM ITS FIRST CLIENT when that client has a
     language the dictionary carries — offered, not imposed: the field is
     filled, and can be changed. A case from before 209 left without one reads
     « Langue du dossier à choisir », amber, until it is set. */
  const { lang: readerLang, available } = useTranslation() as { lang: string; available: string[] };
  const langChoices = languageOptions(available, readerLang);
  const lf = form as { id?: number | null; language?: string | null; clIds?: unknown[] };
  const clientRows = Array.isArray(clients) ? (clients as { id?: unknown }[]) : [];
  const firstClientLang = (() => {
    const first = Array.isArray(lf.clIds) ? lf.clIds[0] : undefined;
    const c = first == null ? undefined : clientRows.find((x) => x.id === first);
    const code = c ? normalizeLang(storedClientLanguage(c)) : null;
    return code && available.includes(code) ? code : null;
  })();
  /* The case's language as stored — or, on a NEW case with none chosen yet, its
     first client's, offered: shown in the field, saved with the case unless
     changed. An existing case is never given one here. */
  const caseLang = storedCaseLanguage(lf) ?? (lf.id ? null : firstClientLang);
  // 140 §2.3 [RULED, Laurent, 2026-08-27] — the switch. Choosing a real
  // status on a tagged ROW is NOT a status edit: it fires the convert route.
  // Getting this wrong is the whole risk of the unit — the control looks
  // like a PUT and is not one. The ordering is §2.4's safety mechanism:
  // convert flips the tag + allocates the ref immediately (no Graph call);
  // the folder step waits in onExpire until the 20s window closes; an undo
  // inside the window therefore costs nothing — no Graph call was ever made.
  const convertProspect = (picked: string) => {
    if (!form.id) return;
    // Captured before the mutation: what the row was, to hand back on undo.
    const snapshot = {
      status: (form.status as string) || "active",
      pendingDate: isoDay(form.pendingDate),
      closedDate: isoDay(form.closedDate),
      archivedDate: isoDay(form.archivedDate),
      // 209 §23 — the moment the status was set, whole (not a day): an undone
      // conversion gives it back with the status
      statusDate: (form as { statusDate?: string | null }).statusDate ?? null,
    };
    // Shared by the three closures below (same invocation scope, no hook):
    // `done` is the convert round-trip, `untagged` the ids §2.5's untag hit —
    // undo needs them back, and both undo and finalize must wait for `done`
    // so a fast Undo click cannot outrun the convert response.
    const ctx: { done: Promise<ConvertedCaseRow | null> | null; untagged: number[] } = {
      done: null,
      untagged: [],
    };
    const mergeRow = (row: ConvertedCaseRow) => {
      setCases((prev: unknown) =>
        Array.isArray(prev)
          ? prev.map((c: ConvertedCaseRow) => (c.id === form.id ? { ...c, ...row } : c))
          : prev,
      );
      setForm((p: ConvertedCaseRow) => ({
        ...p,
        ref: row.ref ?? p.ref,
        status: row.status ?? p.status,
        isProspect: !!row.isProspect,
        formerRef: row.formerRef ?? null,
        pendingDate: isoDay(row.pendingDate),
        closedDate: isoDay(row.closedDate),
        archivedDate: isoDay(row.archivedDate),
      }));
    };
    confirmAndUndo({
      title: [form.ref, form.title].filter(Boolean).join(" — "),
      message: t("case.convert.confirm", {
        fallback:
          "This will transform the Prospect into a real case with a casefile. Continue?",
      }),
      confirmLabel: t("common.continue", { fallback: "Continue" }),
      onConfirm: () => {
        ctx.done = (convertCase(form.id, { status: picked }) as Promise<{
          case?: ConvertedCaseRow;
          untaggedClientIds?: number[];
        } | null>)
          .then((out: { case?: ConvertedCaseRow; untaggedClientIds?: number[] } | null) => {
            const row = out?.case;
            if (!row) return null;
            ctx.untagged = out?.untaggedClientIds || [];
            mergeRow(row);
            return row;
          })
          .catch((e: Error) => {
            toast(e.message, "error");
            return null;
          });
      },
      undo: {
        snapshot,
        restore: (snap: unknown) => {
          const s = snap as typeof snapshot;
          Promise.resolve(ctx.done).then((converted) => {
            if (!converted) return; // the convert itself failed — nothing to invert
            (convertCaseUndo(form.id, { ...s, retagClientIds: ctx.untagged }) as Promise<{
              case?: ConvertedCaseRow;
            } | null>)
              .then((out: { case?: ConvertedCaseRow } | null) => {
                if (out?.case) mergeRow(out.case);
              })
              .catch((e: Error) => toast(e.message, "error"));
          });
        },
      },
      toastName: t("case.convert.toast_undo", {
        ref: form.ref,
        fallback: `Converting ${form.ref} into a real case`,
      }),
      // §2.3 — 5s as written, 20 for this call site: the duration is comfort,
      // not safety (the ordering above is the safety); Laurent adjusts at the
      // glance.
      windowMs: 20000,
      onExpire: () => {
        Promise.resolve(ctx.done).then((converted) => {
          if (!converted) return;
          (convertCaseFinalize(form.id) as Promise<{
            ref?: string;
            folder?: { did?: string };
          } | null>)
            .then((r: { ref?: string; folder?: { did?: string } } | null) => {
              const did = r?.folder?.did;
              const folderTxt =
                did === "renamed"
                  ? t("case.convert.folder_renamed", { fallback: "casefile folder renamed" })
                  : did === "created"
                    ? t("case.convert.folder_created", { fallback: "casefile folder created" })
                    : did === "already_named"
                      ? t("case.convert.folder_kept", { fallback: "casefile folder already up to date" })
                      : t("case.convert.folder_skipped", { fallback: "casefile folder not updated" });
              toast(
                `${t("case.convert.done", { ref: r?.ref || "", fallback: `Conversion final — now case ${r?.ref || ""}` })} — ${folderTxt}`,
                "success",
              );
            })
            .catch((e: Error) => toast(e.message, "error"));
        });
      },
    });
  };
  return (
          <ModalLayout
            title={form.id ? _mt("Edit Case") : _mt("New Case")}
            onClose={cm}
            onSave={() => {
              if (!form.title) {
                toast(
                  t("toast.title_required", {
                    fallback: "⚠ Title is required",
                  }),
                );
                return;
              }
              if (!form.ref) {
                toast(
                  t("toast.reference_required", {
                    fallback: "⚠ Reference is required",
                  }),
                );
                return;
              }
              // 209 §5 [RULED 18] — a new case is not created without its language.
              if (!lf.id && !caseLang) {
                toast(
                  t("toast.case_language_required", {
                    fallback: "⚠ The case's language is required",
                  }),
                );
                return;
              }
              const cd = {
                ...form,
                clIds: form.clIds || [],
                opIds: form.opIds || [],
                ocuIds: form.ocuIds || [],
                exIds: form.exIds || [],
                cpIds: (form.cpIds || []).filter((id: any) =>
                  contactPersons.some((p: any) => p.id === id),
                ),
                ocIds: form.ocIds || [],
                meIds: form.meIds || [],
                arIds: form.arIds || [],
                op2Ids: form.op2Ids || [],
                juId: form.juId ? +form.juId : null,
                docs: form.docs || [],
                status: form.status || "active",
                // 209 §5 — the language shown is the language saved (an offered one included)
                language: caseLang,
              };
              // Brief 48 §2 — the block that stood here added each RP to
              // `caseCollaborators` on `!form.id`. It was the materialise
              // ruling already half-built: client-side, cases only, and only
              // when the RP was set in this same save, so a case whose RP
              // changed later kept an RP outside its own team. The rule lives
              // at the server boundary now (server/src/lib/caseTeam.js), where
              // the collective-case routes and the xlsx importer reach it too.
              // It also froze a ratio; the server writes null, which resolves
              // through the collaborator's live default instead.
              if (form.id) {
                persist(
                  SK.ca,
                  cases.map((c: any) => (c.id === form.id ? { ...c, ...cd } : c)),
                  setCases,
                );
                toast(t("toast.case_updated", { fallback: "Case updated" }));
              } else {
                persist(SK.ca, [...cases, { ...cd, id: tempId() }], setCases);
                toast(t("toast.case_added", { fallback: "Case added" }));
              }
              cm();
            }}
          >
              <>
                <div
                  className="field-row"
                  style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
                >
                  {/* 110 §5 — "2024/003" read like a value (it was a raw
                      literal, never even a key); the hint says it is one. */}
                  {F(
                    "Ref *",
                    "ref",
                    t("modal.ca.ref_ph_hint", { fallback: "e.g. 2026/001" }),
                    1,
                  )}
                  <>
                    {/* 140 §2.3 [RULED, Laurent, 2026-08-27] — while the ROW
                        is tagged, a synthetic Prospect option sits selected
                        on top of the real statuses (which stay below it,
                        unchanged), and choosing a real status fires the
                        convert flow above instead of a status edit. Ruling 6
                        stands: the switch lives in the edit modal, and this
                        dropdown IS the affordance.

                        A tagged DRAFT (the + prospect prefill, no id yet) is
                        not a row and cannot convert — it keeps today's select
                        exactly: plain statuses, tag riding along orthogonally
                        until first save. */}
                    <FieldSelect
                      label={_fl("Status")}
                      value={
                        form.id && form.isProspect
                          ? "__prospect__"
                          : form.status
                      }
                      fallback="active"
                      onChange={(v) => {
                        const td = new Date().toISOString().slice(0, 10);
                        if (form.id && form.isProspect) {
                          if (v === "__prospect__") return;
                          if (
                            v === "archived" &&
                            form.status !== "closed" &&
                            form.status !== "archived"
                          ) {
                            toast(
                              t("toast.only_closed_archive", {
                                fallback: "⚠ Only closed cases can be archived",
                              }),
                            );
                            return;
                          }
                          convertProspect(v);
                          return;
                        }
                        if (
                          v === "archived" &&
                          form.status !== "closed" &&
                          form.status !== "archived"
                        ) {
                          toast(
                            t("toast.only_closed_archive", {
                              fallback: "⚠ Only closed cases can be archived",
                            }),
                          );
                          return;
                        }
                        setForm((p: any) => ({
                          ...p,
                          status: v,
                          pendingDate:
                            v === "pending"
                              ? p.pendingDate || td
                              : p.pendingDate,
                          closedDate:
                            v === "closed"
                              ? p.closedDate || td
                              : v !== "closed" && v !== "archived"
                                ? undefined
                                : p.closedDate,
                          archivedDate:
                            v === "archived"
                              ? p.archivedDate || td
                              : p.archivedDate,
                        }));
                      }}
                      options={[
                        ...(form.id && form.isProspect
                          ? [
                              [
                                "__prospect__",
                                t("matters.prospect.badge", { fallback: "Prospect" }),
                              ] as const,
                            ]
                          : []),
                        /* 108 §6b — the divider is the menu's own row now; the
                           `── … ──` string was the native select's way of
                           drawing one. */
                        ...(caseStatusesList || []).map(
                          ([v, l]: any) => [String(v), String(l ?? "")] as const,
                        ),
                      ]}
                    />
                  </>
                  {S(
                    "Type",
                    "ctype",
                    [["", "—"], ...caseTypesList],
                    caseTypeEntries,
                  )}
                </div>
                {F("Title *", "title")}
                {/* 209 §5 [RULED 18] — the case's language: required for a new
                    case; an existing one without reads « to choose », amber. */}
                <div className="field-row" style={{ gridTemplateColumns: "1fr 2fr" }}>
                  <FieldSelect
                    label={`${t("case.language.label", { fallback: "Case language" })}${lf.id ? "" : " *"}`}
                    value={caseLang ?? ""}
                    onChange={(v) => { updateField("language", v || null); }}
                    options={[["", "—"], ...langChoices]}
                  />
                  {!caseLang && lf.id && (
                    <div style={{ alignSelf: "end", paddingBottom: 8, color: "var(--am)", fontSize: FS.s10_5, fontWeight: 600 }}>
                      {t("case.language.to_choose", { fallback: "Case language to choose" })}
                    </div>
                  )}
                </div>
                {(() => {
                  const _caCtxModal = form.id ? { caseId: form.id } : {};
                  const _canTermsModal =
                    !!_appIsAdmin ||
                    _appPerms.can("caseView.terms.view", _caCtxModal);
                  const _canFinModal =
                    !!_appIsAdmin ||
                    _appPerms.can("caseView.financials.view", _caCtxModal);
                  const costsR = form.costsRatio ?? 19;
                  const refR = form.referenceHourlyRate ?? 0;
                  // P17.1a (F2) — the rate card is server-authoritative: exVAT /
                  // vatIncl are served (firm vatMul, not a hardcoded *1.21) via the
                  // caseModalCalc round-trip, "—" until it lands, like Bruto/Netto
                  // below. The inverse-solve inputs (rcUpd) stay browser-side —
                  // see the PR note on the vatRate≠21 interaction.
                  const _cmc =
                    caseModalCalc && caseModalCalc.id === (form.id ?? null)
                      ? caseModalCalc.data
                      : null;
                  const exVAT = _cmc ? _cmc.exVAT : null;
                  const vatIncl = _cmc ? _cmc.vatIncl : null;
                  const rcUpd = (field: any, n: any) => {
                    if (isNaN(n)) return;
                    if (field === "ref")
                      updateField("referenceHourlyRate", Math.round(n * 100) / 100);
                    else if (field === "exvat")
                      updateField(
                        "referenceHourlyRate",
                        Math.round((n / (1 + costsR / 100)) * 100) / 100,
                      );
                    else if (field === "vatincl")
                      updateField(
                        "referenceHourlyRate",
                        Math.round((n / 1.21 / (1 + costsR / 100)) * 100) / 100,
                      );
                    else if (field === "ratio")
                      updateField("costsRatio", Math.round(n * 100) / 100);
                  };
                  /* 207C §2 (extract, rule 3) — the rate card's field is src/components/AmountField.tsx,
                     moved there unchanged; the closure stays as its forwarder, so the four
                     call sites below read as they did. 207C §2 (widened) — the field's look is
                     the rate card's own, handed in; an empty text hands up null, which the
                     rate card ignores as it ignored an unparsable one. */
                  const rcI = (field: string, val: number | null, suffix: React.ReactNode) => (
                    <AmountField
                      value={val}
                      suffix={suffix}
                      style={{ flex: 1, padding: "4px 6px", fontSize: FS.s11_5 }}
                      onAmount={(n) => {
                        if (n !== null) rcUpd(field, n);
                      }}
                    />
                  );
                  // C.2 — server-handed estimate (owning-id-stamped; "—" via
                  // fq(null) until the first response lands). _cmc is hoisted
                  // above (P17.1a) so the rate card reads the same served data.
                  const bruto = _cmc ? _cmc.bruto : null;
                  const netto = _cmc ? _cmc.netto : null;
                  const gr = _cmc ? _cmc.gr : null;
                  const rdO = (val: any, red: any) => (
                    <div
                      style={{
                        flex: 1,
                        padding: "4px 6px",
                        fontSize: FS.s11_5,
                        fontFamily: "'JetBrains Mono',monospace",
                        background: "var(--bc)",
                        border: "1px solid var(--br)",
                        borderRadius: RAD.r8,
                        color: red && val < 0 ? "var(--rd)" : "var(--ts)",
                      }}
                    >
                      {fq(val)}
                    </div>
                  );
                  return (
                    <>
                      <div className="field">
                        <label>
                          {_fl(
                            t("modal.case.claim_amount", {
                              fallback: "Claim Amount",
                            }),
                          )}
                        </label>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          {/* 208 §10 — the one amount field (src/components/AmountField.tsx): typed with a decimal comma, shown « 1 234,50 » */}
                          <AmountField
                            value={form.claimAmount}
                            empty=""
                            inputMode="decimal"
                            onAmount={(n) => updateField("claimAmount", n)}
                            style={{
                              flex: 1,
                              padding: "4px 6px",
                              fontSize: FS.s11_5,
                            }}
                            placeholder="0"
                          />
                          <span
                            style={{
                              fontSize: FS.s10,
                              color: "var(--tm)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t("common.currency_eur", { fallback: "EUR" })}
                          </span>
                        </div>
                      </div>
                      {_canTermsModal && (
                        <div
                          style={{
                            border: "1px solid var(--br)",
                            borderRadius: RAD.r8,
                            padding: "10px 12px",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              fontSize: FS.s9_5,
                              textTransform: "uppercase",
                              letterSpacing: ".4px",
                              color: "var(--tm)",
                              fontWeight: 600,
                              marginBottom: 8,
                            }}
                          >
                            {t("modal.case.terms", { fallback: "Terms" })}
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "8px 12px",
                              marginBottom: 6,
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: FS.s10,
                                  color: "var(--ts)",
                                  marginBottom: 3,
                                }}
                              >
                                {t("modal.case.reference_hourly_rate", {
                                  fallback: "Reference Hourly Rate",
                                })}
                              </div>
                              {rcI(
                                "ref",
                                refR,
                                t("common.eur_per_hour", {
                                  fallback: "EUR/h",
                                }),
                              )}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: FS.s10,
                                  color: "var(--ts)",
                                  marginBottom: 3,
                                }}
                              >
                                {t("modal.case.costs_ratio_pct", {
                                  fallback: "Costs Ratio %",
                                })}
                              </div>
                              {rcI("ratio", costsR, "%")}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: FS.s10,
                                  color: "var(--ts)",
                                  marginBottom: 3,
                                }}
                              >
                                {t("modal.case.total_hourly_cost_exvat", {
                                  fallback: "Total Hourly Cost ExVAT",
                                })}
                              </div>
                              {rcI(
                                "exvat",
                                exVAT,
                                t("common.eur_per_hour", {
                                  fallback: "EUR/h",
                                }),
                              )}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: FS.s10,
                                  color: "var(--ts)",
                                  marginBottom: 3,
                                }}
                              >
                                {t("modal.case.total_hourly_cost_vatincl", {
                                  fallback: "Total Hourly Cost VAT Incl.",
                                })}
                              </div>
                              {rcI(
                                "vatincl",
                                vatIncl,
                                t("common.eur_per_hour", {
                                  fallback: "EUR/h",
                                }),
                              )}
                            </div>
                          </div>
                          <p
                            style={{
                              fontSize: FS.s9_5,
                              color: "var(--tm)",
                              margin: 0,
                            }}
                          >
                            {t("modal.case.terms_help", {
                              fallback:
                                "Adjust any field to calculate the others. Costs ratio stays fixed.",
                            })}
                          </p>
                        </div>
                      )}
                      {_canFinModal && (
                        <div
                          style={{
                            border: "1px solid var(--br)",
                            borderRadius: RAD.r8,
                            padding: "10px 12px",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              fontSize: FS.s9_5,
                              textTransform: "uppercase",
                              letterSpacing: ".4px",
                              color: "var(--tm)",
                              fontWeight: 600,
                              marginBottom: 8,
                            }}
                          >
                            {t("case.claim.success_fee", {
                              fallback: "Success Fee",
                            })}
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "8px 12px",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: FS.s10,
                                  color: "var(--ts)",
                                  marginBottom: 3,
                                }}
                              >
                                {t("modal.case.success_fee_rate_pct", {
                                  fallback: "Success Fee Rate %",
                                })}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  className="field-input"
                                  value={successFeeRateOf(form)}
                                  onChange={(e) =>
                                    updateField("successFeeRate", +e.target.value)
                                  }
                                  style={{
                                    flex: 1,
                                    padding: "4px 6px",
                                    fontSize: FS.s11_5,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: FS.s10,
                                    color: "var(--tm)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  %
                                </span>
                              </div>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: FS.s10,
                                  color: "var(--ts)",
                                  marginBottom: 3,
                                }}
                              >
                                {t("modal.case.bruto_success_fee_est", {
                                  fallback: "Bruto Success Fee Est.",
                                })}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                {rdO(bruto, false)}
                                <span
                                  style={{
                                    fontSize: FS.s10,
                                    color: "var(--tm)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("common.currency_eur", {
                                    fallback: "EUR",
                                  })}
                                </span>
                              </div>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: FS.s10,
                                  color: "var(--ts)",
                                  marginBottom: 3,
                                }}
                              >
                                {t("modal.case.netto_success_fee_est", {
                                  fallback: "Netto Success Fee Est.",
                                })}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                {rdO(netto, true)}
                                <span
                                  style={{
                                    fontSize: FS.s10,
                                    color:
                                      netto < 0 ? "var(--rd)" : "var(--tm)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("common.currency_eur", {
                                    fallback: "EUR",
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                          {form.id && gr > 0 && (
                            <p
                              style={{
                                fontSize: FS.s9,
                                color: "var(--tm)",
                                margin: "6px 0 0",
                              }}
                            >
                              {t("modal.case.netto_help", {
                                total: fc(gr),
                                fallback: `Netto = Bruto minus running Grand Total (${fc(gr)}).`,
                              })}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
                <div
                  className="field-row"
                  style={
                    form.status === "pending" ||
                    form.status === "closed" ||
                    form.status === "archived"
                      ? { gridTemplateColumns: "1fr 1fr 1fr" }
                      : {}
                  }
                >
                  <div className="field">
                    <label>{_fl("Open Date")}</label>
                    <input
                      className="field-input"
                      type="date"
                      value={(form.openDate || "").slice(0, 10)}
                      onChange={(e) => updateField("openDate", e.target.value)}
                    />
                  </div>
                  {form.status === "pending" && (
                    <div className="field">
                      <label>{_fl("Suspended Since")}</label>
                      <input
                        className="field-input"
                        type="date"
                        value={(form.pendingDate || "").slice(0, 10)}
                        onChange={(e) => updateField("pendingDate", e.target.value)}
                      />
                    </div>
                  )}
                  {(form.status === "closed" || form.status === "archived") && (
                    <div className="field">
                      <label>{_fl("Closed On")}</label>
                      <input
                        className="field-input"
                        type="date"
                        value={(form.closedDate || "").slice(0, 10)}
                        onChange={(e) => updateField("closedDate", e.target.value)}
                      />
                    </div>
                  )}
                  {form.status === "archived" && (
                    <div className="field">
                      <label>{_fl("Archived On")}</label>
                      <input
                        className="field-input"
                        type="date"
                        value={(form.archivedDate || "").slice(0, 10)}
                        onChange={(e) => updateField("archivedDate", e.target.value)}
                      />
                    </div>
                  )}
                  <div className="field">
                    <label>{_fl("Jurisdiction")}</label>
                    <Combobox
                      value={form.juId ? String(form.juId) : ""}
                      onChange={(v) => updateField("juId", v ? +v : "")}
                      options={jurisdictions.map((j: any) => ({
                        value: String(j.id),
                        label: j.name || "(unnamed)",
                        sub: j.city || "",
                      }))}
                      placeholder={t("common.select_dots", {
                        fallback: "Select...",
                      })}
                    />
                  </div>
                  <div className="field">
                    <label>
                      {t("case.field.introductionDate", {
                        fallback: "Introduction date",
                      })}
                    </label>
                    <input
                      className="field-input"
                      type="date"
                      value={(form.introductionDate || "").slice(0, 10)}
                      onChange={(e) => updateField("introductionDate", e.target.value)}
                    />
                  </div>
                </div>
                <div className="field">
                  {/* AUTHORITY (155 §4): this folder block and the cc modal's
                      twin (CollectiveCaseView.tsx, same field) differ
                      deliberately by scope ctx (caseId vs ccId); the
                      link/permission wiring must change in BOTH. */}
                  <label>{_fl("SharePoint / OneDrive Folder")}</label>
                  {(() => {
                    const _caCtx = form.id ? { caseId: form.id } : {};
                    const _canEditFolder = _appPerms.can(
                      "caseView.documents.link",
                      _caCtx,
                    );
                    const _canLinkFolder = _appPerms.can(
                      "caseView.documents.link",
                      _caCtx,
                    );
                    const _canViewFolder = _appPerms.can(
                      "caseView.documents.view",
                      _caCtx,
                    );
                    if (form.caseRootFolder) {
                      const _nm =
                        form.caseRootFolder.split("/").filter(Boolean).pop() ||
                        form.caseRootFolder;
                      const _open = async () => {
                        try {
                          // 174 §1.2 — through api(): BOTH credentials (the
                          // lx:token Bearer and the lx_session cookie), not
                          // the cookie alone. api() supplies the /api prefix.
                          const j = (await api.get(
                            "/auth/microsoft/browse?path=" +
                              encodeURIComponent(form.caseRootFolder) +
                              "&caseId=" +
                              form.id +
                              "&mode=resolve",
                          )) as { currentFolder?: { webUrl?: string } };
                          if (j.currentFolder?.webUrl) {
                            window.open(j.currentFolder.webUrl, "_blank");
                            return;
                          }
                          toast(
                            t("toast.cannot_resolve_folder", {
                              fallback: "⚠ Couldn't resolve folder link",
                            }),
                          );
                        } catch (e2: any) {
                          toast(
                            "⚠ " +
                              (e2.message ||
                                t("toast.open_failed", {
                                  fallback: "Open failed",
                                })),
                          );
                        }
                      };
                      return (
                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            alignItems: "center",
                            flexWrap: "wrap",
                            padding: "5px 8px",
                            background: "var(--bi)",
                            border: "1px solid var(--br)",
                            borderRadius: RAD.r6,
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontSize: FS.s11,
                              color: "var(--tp)",
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={form.caseRootFolder}
                          >
                            <Folder
                              size={11}
                              style={{ color: "var(--ac)", flexShrink: 0 }}
                            />
                            {_nm}
                          </span>
                          {_canViewFolder && (
                            <button
                              type="button"
                              className="btn btn-ghost"
                              style={{
                                padding: "2px 7px",
                                fontSize: FS.s10,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                              title={t("modal.case.open_in_sharepoint", {
                                fallback: "Open folder in SharePoint",
                              })}
                              onClick={_open}
                            >
                              <ExtLink2 size={10} />
                              {t("common.open", { fallback: "Open" })}
                            </button>
                          )}
                          {_canLinkFolder && (
                            <>
                              <button
                                type="button"
                                className="btn btn-ghost"
                                style={{
                                  padding: "2px 6px",
                                  fontSize: FS.s10,
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                                title={t("modal.case.relink_folder", {
                                  fallback: "Re-link to a different folder",
                                })}
                                onClick={async () => {
                                  try {
                                    const _lid = +form.id || null;
                                    const picked = await openOneDrivePicker({
                                      mode: "link",
                                      caseId:
                                        _lid && cases.some((c: any) => c.id === _lid)
                                          ? _lid
                                          : null,
                                      ccId:
                                        _lid && collectiveCases.some((c: any) => c.id === _lid)
                                          ? _lid
                                          : null,
                                    });
                                    if (picked?.isFolder) {
                                      const parentPath = picked.raw
                                        ?.parentReference?.path
                                        ? picked.raw.parentReference.path.replace(
                                            /^.*?root:/,
                                            "",
                                          )
                                        : "";
                                      updateField(
                                        "caseRootFolder",
                                        (parentPath ? parentPath + "/" : "/") +
                                          picked.name,
                                      );
                                    }
                                  } catch (e2: any) {
                                    toast(
                                      "⚠ " +
                                        (e2.message ||
                                          t("toast.picker_failed", {
                                            fallback: "Picker failed",
                                          })),
                                    );
                                  }
                                }}
                              >
                                <Pencil size={10} />
                              </button>
                              <button
                                type="button"
                                className="btn"
                                style={{
                                  padding: "2px 6px",
                                  fontSize: FS.s10,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  background: "rgba(248,113,113,.1)",
                                  color: "var(--rd)",
                                  border: "1px solid rgba(248,113,113,.3)",
                                }}
                                title={t("modal.case.unlink_folder", {
                                  fallback: "Unlink folder from this case",
                                })}
                                onClick={() => updateField("caseRootFolder", null)}
                              >
                                <X size={10} />
                              </button>
                            </>
                          )}
                          {!_canViewFolder && !_canLinkFolder && (
                            <span
                              style={{
                                fontSize: FS.s9_5,
                                color: "var(--tm)",
                                fontStyle: "italic",
                              }}
                            >
                              {t("common.read_only", { fallback: "Read-only" })}
                            </span>
                          )}
                        </div>
                      );
                    }
                    if (!_canEditFolder && !_canLinkFolder)
                      return (
                        <span
                          style={{
                            fontSize: FS.s10,
                            color: "var(--tm)",
                            fontStyle: "italic",
                          }}
                        >
                          {t("modal.case.no_folder_no_permission", {
                            fallback:
                              "No folder linked. You don't have permission to create or link a folder.",
                          })}
                        </span>
                      );
                    return (
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {_canEditFolder && (
                          <AddButton
                            label={
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <Folder size={10} />
                                {t("modal.case.create_case_folder", {
                                  fallback: "Create Case Folder",
                                })}
                              </span>
                            }
                            onClick={async () => {
                              try {
                                // 174 §1.2 — through api(): BOTH credentials,
                                // plus the X-LexCRM a cookie-auth mutation
                                // needs. api() supplies the /api prefix.
                                const j = (await api.post(
                                  "/auth/microsoft/create-case-folder",
                                  { ref: form.ref, title: form.title },
                                )) as {
                                  path?: string;
                                  existed?: boolean;
                                  name?: string;
                                };
                                updateField("caseRootFolder", j.path);
                                toast(
                                  j.existed
                                    ? t("toast.folder.linked_to_existing", {
                                        name: j.name || "",
                                        fallback:
                                          "Linked to existing folder: " +
                                          (j.name || ""),
                                      })
                                    : t("toast.folder.created", {
                                        name: j.name || "",
                                        fallback:
                                          "Created new folder: " +
                                          (j.name || ""),
                                      }),
                                );
                              } catch (e2: any) {
                                toast(
                                  "⚠ " +
                                    (e2.message ||
                                      t("toast.folder_create_failed", {
                                        fallback: "Folder create failed",
                                      })),
                                );
                              }
                            }}
                            style={
                              !form.ref && !form.title
                                ? { opacity: 0.5, pointerEvents: "none" }
                                : undefined
                            }
                          />
                        )}
                        {_canLinkFolder && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            style={{
                              padding: "2px 7px",
                              fontSize: FS.s10,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                            onClick={async () => {
                              try {
                                const _lid = +form.id || null;
                                const picked = await openOneDrivePicker({
                                  mode: "link",
                                  caseId:
                                    _lid && cases.some((c: any) => c.id === _lid)
                                      ? _lid
                                      : null,
                                  ccId:
                                    _lid && collectiveCases.some((c: any) => c.id === _lid)
                                      ? _lid
                                      : null,
                                });
                                if (picked?.webUrl && picked.isFolder) {
                                  const parentPath = picked.raw?.parentReference
                                    ?.path
                                    ? picked.raw.parentReference.path.replace(
                                        /^.*?root:/,
                                        "",
                                      )
                                    : "";
                                  updateField(
                                    "caseRootFolder",
                                    (parentPath ? parentPath + "/" : "/") +
                                      picked.name,
                                  );
                                  toast(
                                    t("toast.linked_named", {
                                      name: picked.name,
                                      fallback: "Linked: " + picked.name,
                                    }),
                                  );
                                } else if (picked && !picked.isFolder) {
                                  toast(
                                    t("toast.select_folder_not_file", {
                                      fallback:
                                        "⚠ Select a folder, not a file",
                                    }),
                                  );
                                }
                              } catch (e2: any) {
                                toast(
                                  "⚠ " +
                                    (e2.message ||
                                      t("toast.picker_failed", {
                                        fallback: "Picker failed",
                                      })),
                                );
                              }
                            }}
                          >
                            <Link2 size={10} />
                            {t("modal.case.link_existing_folder", {
                              fallback: "Link existing folder",
                            })}
                          </button>
                        )}
                        <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                          {t("modal.case.no_folder_yet", {
                            fallback: "No folder linked yet",
                          })}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <MultiSelect
                  l={t("case.responsible_partner", {
                    fallback: "Responsible Partner",
                  })}
                  opts={choosableCo(collaborators, form.responsiblePartners).filter(
                    (c: any) => (c.role || "").toLowerCase() === "partner",
                  )}
                  sel={form.responsiblePartners || []}
                  onChange={(v: any) => updateField("responsiblePartners", v)}
                  locked={!_appIsAdmin}
                />
                {/* 108 §8a — ~3× the browser-default two-line box. */}
                {T("Description", "desc", 6)}
                {/* 189 §B — the firm's own fields, after the built-in ones. */}
                <CustomFieldsBlock
                  entity="case"
                  values={(form as { customFields?: Record<string, unknown> | null }).customFields}
                  onChange={(v) => { updateField("customFields", v); }}
                />
                {form.id && (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginTop: 16,
                      paddingTop: 14,
                      paddingBottom: 2,
                      borderTop: "1px solid var(--br)",
                      position: "sticky",
                      bottom: -12,
                      background: "var(--bs)",
                      zIndex: Z.stickyCell,
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        fontSize: FS.s11,
                        fontWeight: 600,
                        borderRadius: RAD.r8,
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        background:
                          form.status === "archived"
                            ? "rgba(74,124,255,.15)"
                            : "rgba(139,69,19,.15)",
                        color:
                          form.status === "archived" ? "var(--ac)" : CT.c_b87333,
                      }}
                      onClick={() => {
                        const td = new Date().toISOString().slice(0, 10);
                        if (form.status === "archived") {
                          setForm((p: any) => ({
                            ...p,
                            status: p._prevStatus || "active",
                            archivedDate: undefined,
                          }));
                        } else {
                          setForm((p: any) => ({
                            ...p,
                            _prevStatus: p.status,
                            status: "archived",
                            archivedDate: td,
                            closedDate:
                              p.closedDate ||
                              (p.status !== "closed" ? td : p.closedDate),
                          }));
                        }
                      }}
                    >
                      {form.status === "archived"
                        ? t("common.unarchive", { fallback: "Unarchive" })
                        : t("common.archive", { fallback: "Archive" })}
                    </button>
                    <button
                      type="button"
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        fontSize: FS.s11,
                        fontWeight: 600,
                        borderRadius: RAD.r8,
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        background: "rgba(248,113,113,.15)",
                        color: "var(--rd)",
                      }}
                      onClick={() => {
                        if (form.status !== "archived") {
                          toast(
                            t("toast.archive_first", {
                              fallback:
                                "⚠ Archive the case first. Only archived cases can be deleted.",
                            }),
                          );
                          return;
                        }
                        confirmAndUndo({
                          label: `case ${form.ref}`,
                          onConfirm: () => {
                            persist(
                              SK.ca,
                              cases.filter((c: any) => c.id !== form.id),
                              setCases,
                            );
                            cm();
                            toast(
                              t("toast.case_deleted", {
                                fallback: "Case deleted",
                              }),
                            );
                          },
                          undo: null,
                        });
                      }}
                    >
                      {t("common.delete_caps", { fallback: "DELETE" })}
                    </button>
                  </div>
                )}
              </>
          </ModalLayout>
  );
}
